import React, { useMemo } from "react";
import { Input } from "./ui/input";

/**
 * Country codes we support in the phone-number picker. India (+91) is the
 * default. Kept intentionally short — top 20 by candidate origin.
 */
export const COUNTRY_CODES = [
  { code: "+91", name: "India", flag: "🇮🇳" },
  { code: "+1", name: "USA / Canada", flag: "🇺🇸" },
  { code: "+44", name: "United Kingdom", flag: "🇬🇧" },
  { code: "+61", name: "Australia", flag: "🇦🇺" },
  { code: "+65", name: "Singapore", flag: "🇸🇬" },
  { code: "+971", name: "UAE", flag: "🇦🇪" },
  { code: "+49", name: "Germany", flag: "🇩🇪" },
  { code: "+33", name: "France", flag: "🇫🇷" },
  { code: "+31", name: "Netherlands", flag: "🇳🇱" },
  { code: "+46", name: "Sweden", flag: "🇸🇪" },
  { code: "+353", name: "Ireland", flag: "🇮🇪" },
  { code: "+81", name: "Japan", flag: "🇯🇵" },
  { code: "+82", name: "South Korea", flag: "🇰🇷" },
  { code: "+86", name: "China", flag: "🇨🇳" },
  { code: "+63", name: "Philippines", flag: "🇵🇭" },
  { code: "+92", name: "Pakistan", flag: "🇵🇰" },
  { code: "+880", name: "Bangladesh", flag: "🇧🇩" },
  { code: "+60", name: "Malaysia", flag: "🇲🇾" },
  { code: "+27", name: "South Africa", flag: "🇿🇦" },
  { code: "+55", name: "Brazil", flag: "🇧🇷" },
];

const DEFAULT_CODE = "+91";
const MAX_DIGITS = 10;

/**
 * Parse a stored value like "+91 9876543210" into {code, digits}.
 * Falls back to (+91, "") for anything unrecognised.
 */
export function parsePhone(value) {
  if (!value || typeof value !== "string") return { code: DEFAULT_CODE, digits: "" };
  const m = value.match(/^\+(\d{1,4})\s+(\d{1,15})$/);
  if (!m) return { code: DEFAULT_CODE, digits: "" };
  return { code: `+${m[1]}`, digits: m[2] };
}

/**
 * Format {code, digits} back to the canonical "+91 9876543210" string. Returns
 * an empty string when no digits are entered.
 */
export function formatPhone(code, digits) {
  const d = String(digits || "").replace(/\D/g, "").slice(0, 15);
  if (!d) return "";
  return `${code} ${d}`;
}

/**
 * A reusable phone-number input with a country-code dropdown.
 * Emits its value through onChange as a single canonical string, or "" when empty.
 */
export default function PhoneNumberInput({
  value,
  onChange,
  disabled,
  testIdPrefix = "phone",
  placeholder = "Enter phone number",
  className = "",
}) {
  const { code, digits } = useMemo(() => parsePhone(value), [value]);

  const handleDigitsChange = (raw) => {
    const cleaned = raw.replace(/\D/g, "").slice(0, MAX_DIGITS);
    onChange?.(formatPhone(code, cleaned));
  };

  const handleCodeChange = (newCode) => {
    onChange?.(formatPhone(newCode, digits));
  };

  return (
    <div className={`flex gap-2 ${className}`} data-testid={`${testIdPrefix}-wrapper`}>
      <select
        value={code}
        onChange={(e) => handleCodeChange(e.target.value)}
        disabled={disabled}
        data-testid={`${testIdPrefix}-country`}
        aria-label="Country code"
        className="h-10 rounded-md border border-zinc-200 bg-white px-2 text-sm w-[110px] focus:outline-none focus:ring-2 focus:ring-[#002FA7]/20 disabled:opacity-60"
      >
        {COUNTRY_CODES.map((c) => (
          <option key={c.code} value={c.code}>
            {c.flag} {c.code}
          </option>
        ))}
      </select>
      <Input
        type="tel"
        inputMode="numeric"
        autoComplete="tel-national"
        maxLength={MAX_DIGITS}
        value={digits}
        onChange={(e) => handleDigitsChange(e.target.value)}
        onKeyDown={(e) => {
          if (
            e.key.length === 1 &&
            !/[0-9]/.test(e.key) &&
            !e.ctrlKey && !e.metaKey && !e.altKey
          ) {
            e.preventDefault();
          }
        }}
        disabled={disabled}
        placeholder={placeholder}
        data-testid={`${testIdPrefix}-number`}
        className="flex-1"
      />
    </div>
  );
}
